import type { QuestionContent, QuestionType } from "./types.ts";

/**
 * Editor-facing metadata per question type. Labels and hints are product copy,
 * so they are Spanish; everything around them stays English.
 *
 * `defaultContent` is what a freshly added question starts from, so the editor
 * always renders something valid.
 */
export type QuestionTypeMeta<T extends QuestionType> = {
  type: T;
  label: string;
  hint: string;
  /** Whether an attempt can be scored without a human. */
  autoGraded: boolean;
  defaultContent: QuestionContent[T];
};

const id = (seed: string) =>
  `${seed}-${Math.random().toString(36).slice(2, 8)}`;

export const questionTypeMeta: {
  [T in QuestionType]: QuestionTypeMeta<T>;
} = {
  multiple_choice: {
    type: "multiple_choice",
    label: "Selección múltiple",
    hint: "Una o varias opciones correctas.",
    autoGraded: true,
    defaultContent: {
      multiple: false,
      options: [
        { text: "Opción 1", correct: true },
        { text: "Opción 2", correct: false },
      ],
    },
  },
  true_false: {
    type: "true_false",
    label: "Verdadero o falso",
    hint: "Una afirmación y su valor de verdad.",
    autoGraded: true,
    defaultContent: { correct: true },
  },
  written: {
    type: "written",
    label: "Respuesta escrita",
    hint: "La corrige un instructor: no se puntúa sola.",
    autoGraded: false,
    defaultContent: { answer: "" },
  },
  sort: {
    type: "sort",
    label: "Ordenar",
    hint: "Poner los elementos en la secuencia correcta.",
    autoGraded: true,
    defaultContent: {
      options: [
        { id: id("opt"), text: "Primero", order: 0 },
        { id: id("opt"), text: "Segundo", order: 1 },
      ],
    },
  },
  match: {
    type: "match",
    label: "Relacionar",
    hint: "Unir cada elemento con su par.",
    autoGraded: true,
    defaultContent: {
      pairs: [
        { id: id("pair"), left: "Elemento 1", right: "Su par" },
        { id: id("pair"), left: "Elemento 2", right: "Su par" },
      ],
    },
  },
  fill_in_the_blank: {
    type: "fill_in_the_blank",
    label: "Completar espacios",
    hint: "Usá {{0}}, {{1}}… para marcar cada hueco.",
    autoGraded: true,
    defaultContent: {
      text: "El resultado de 2 + 2 es {{0}}.",
      blanks: [
        {
          options: [
            { text: "4", correct: true },
            { text: "5", correct: false },
          ],
        },
      ],
    },
  },
};

export const questionTypeList = Object.values(
  questionTypeMeta,
) as QuestionTypeMeta<QuestionType>[];

/** Splits fill-in-the-blank text into literals and `{{n}}` blank markers. */
export function splitBlanks(text: string) {
  return text
    .split(/(\{\{\d+\}\})/g)
    .filter((part) => part !== "")
    .map((part) => {
      const match = /^\{\{(\d+)\}\}$/.exec(part);
      return match
        ? ({ kind: "blank", index: Number(match[1]) } as const)
        : ({ kind: "text", text: part } as const);
    });
}
